<script setup lang="ts">
import { Maximize2, Minimize2 } from '@lucide/vue'

import type { WidgetWrapperMetric } from './types'
import { useFullscreen } from './useFullscreen'
import ActionButton from '@/views/Dashboards/Dashboard/components/ActionButton.vue'
import { computed } from 'vue'

type Props = {
  sectionLabel?: string
  title?: string
  metric?: WidgetWrapperMetric[]
}

const { sectionLabel = '', title = '', metric } = defineProps<Props>()

defineOptions({ inheritAttrs: false })

const { isFullscreen, toggle: toggleFullscreen, exit: exitFullscreen } = useFullscreen()

const isSectionLabelVisible = computed(() => title !== sectionLabel && title !== '')
</script>

<template>
  <div
    v-if="isFullscreen"
    class="fixed inset-0 z-50 bg-scrim"
    @click="exitFullscreen"
  />
  <div
    v-bind="$attrs"
    class="flex flex-col bg-card border border-border rounded-ds-lg"
    :class="isFullscreen ? 'fixed inset-2 z-50 shadow-ds-xl' : 'w-full h-full shadow-ds-md'"
  >
    <div class="flex flex-col gap-2.5 p-4 border-b border-border">
      <div class="flex items-start justify-between gap-3">
        <div
          class="flex flex-col gap-0.5 min-w-0 overflow-hidden"
          :class="isSectionLabelVisible ? 'items-start' : 'items-center'"
        >
          <div
            v-if="isSectionLabelVisible"
            class="text-[10px] font-bold uppercase tracking-ds-caps text-muted-foreground truncate"
          >
            {{ sectionLabel }}
          </div>
          <div
            class="w-full text-lg font-bold tracking-ds-tight truncate"
            :class="!title ? 'text-muted-foreground/75' : 'text-foreground'"
          >
            {{ title || sectionLabel }}
          </div>
        </div>
        <div class="flex items-center gap-2 flex-none">
          <slot name="header" />
          <div
            v-if="metric?.length"
            class="flex flex-col items-end"
            :class="metric?.length > 1 ? '-my-1.75 gap-0.75' : 'gap-2'"
          >
            <div
              v-for="(item, index) in metric"
              :key="index"
              v-tooltip.top="item.tooltip"
              class="flex items-center justify-between gap-2 py-1 px-3 bg-accent rounded-full"
            >
              <component
                :is="item.icon"
                v-if="item.icon"
                class="size-3 text-accent-foreground"
              />
              <span
                v-if="item.label"
                class="text-xs font-medium text-accent-foreground"
              >{{ item.label }}</span>
              <span
                v-if="item.value"
                class="text-[0.8125rem] font-bold tracking-ds-tight text-primary-pressed tabular-nums"
              >
                {{ item.value }}
              </span>
            </div>
          </div>
          <slot name="protect" /> 
          <ActionButton
            :title="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'"
            :aria-label="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'"
            @click="toggleFullscreen"
          >
            <component
              :is="isFullscreen ? Minimize2 : Maximize2"
            />
          </ActionButton>
        </div>
      </div>
      <div
        v-if="$slots.actions"
        class="flex items-center gap-2 py-1.5 -mx-4 -mb-4 px-4 flex-wrap bg-muted/35 border-t border-border/25"
      >
        <!--
          Same reasoning as the desktop header's `#actions` slot — a bare
          outlet, no fallback content, since every real caller has an explicit
          opinion.
        -->
        <slot name="actions" />
      </div>
    </div>
    <div class="min-h-50 p-4 flex-1 flex flex-col rounded-b-ds-lg overflow-hidden">
      <slot>
        <div class="w-full h-full min-h-25 flex items-center justify-center text-center p-4 box-border border border-dashed border-input rounded-ds-md bg-background text-sm font-medium text-muted-foreground">
          Widget content area
        </div>
      </slot>
    </div>
  </div>
</template>
